import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, DollarSign } from 'lucide-react';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface CalendarViewProps {
  rentals: Rental[];
  onRentalClick: (rental: Rental) => void;
  getStatusColor: (status: string) => string;
  getStatusBadgeColor: (status: string) => string;
  getCoastFromLocation: (location: string) => string;
}
export function CalendarView({
  rentals,
  onRentalClick,
  getStatusColor,
  getStatusBadgeColor,
  getCoastFromLocation
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'month' | 'list'>('month');
  const [groupByCoast, setGroupByCoast] = useState(false);
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return rentals.filter(rental => {
      // Check if event is on this date (proposed or alternate)
      if (rental.proposedEventDate === dateStr || rental.alternateDate === dateStr) {
        return true;
      }
      // For multi-day events, check if this date falls within the event duration
      if (rental.selections.numberOfDays > 1 && rental.proposedEventDate) {
        const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        // Check each day of the event
        for (let i = 1; i < rental.selections.numberOfDays; i++) {
          const eventDay = new Date(eventDate);
          eventDay.setDate(eventDay.getDate() + i);
          const eventDayStr = eventDay.toISOString().split('T')[0];
          if (eventDayStr === dateStr) {
            return true;
          }
        }
      }
      return false;
    });
  };
  // Helper function to get actual ring count
  const getActualRingCount = (rental: Rental) => {
    return rental.selections.fullGen3Ring;
  };
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const monthName = currentDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-32 bg-slate-50 border border-slate-200" data-id="element-335"></div>);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const events = getEventsForDate(date);
      const isToday = new Date().toDateString() === date.toDateString();
      days.push(<div key={day} className={`min-h-32 border border-slate-200 p-2 ${isToday ? 'bg-blue-50 border-blue-400' : 'bg-white'}`} data-id="element-336">
          <div className={`text-sm font-semibold mb-2 ${isToday ? 'text-blue-800' : 'text-slate-700'}`} data-id="element-337">
            {day}
          </div>
          <div className="space-y-1" data-id="element-338">
            {events.map((event, idx) => <div key={idx} onClick={() => onRentalClick(event)} className="text-xs bg-white border rounded px-2 py-1 cursor-pointer hover:shadow-md transition-shadow" style={{
            borderLeftWidth: '3px',
            borderLeftColor: getStatusBadgeColor(event.status).replace('bg-', '#').replace('slate-400', '#94a3b8').replace('blue-500', '#3b82f6').replace('yellow-500', '#eab308').replace('green-500', '#22c55e')
          }} data-id="element-339">
                <div className="flex items-start justify-between" data-id="element-340">
                  <div className="flex-1 min-w-0" data-id="element-341">
                    <div className="font-semibold text-slate-800 truncate" data-id="element-342">
                      {event.eventName}
                    </div>
                    <div className="text-slate-600 truncate" data-id="element-343">{event.name}</div>
                    <div className="flex items-center justify-between mt-1" data-id="element-344">
                      <span className={`text-xs px-1 rounded ${getStatusColor(event.status)}`} data-id="element-345">
                        {event.status}
                      </span>
                      <span className="text-slate-500" data-id="element-346">
                        ${event.total.toFixed(0)}
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 font-semibold mt-1" data-id="element-347">
                      {getActualRingCount(event)} Ring
                      {getActualRingCount(event) !== 1 ? 's' : ''}
                      {event.selections.numberOfDays > 1 && <span className="text-purple-600 ml-1" data-id="element-348">
                          ({event.selections.numberOfDays} Days)
                        </span>}
                    </div>
                  </div>
                  {(event.status === 'Deposit' || event.status === 'Paid in Full') && <div className="ml-1 flex-shrink-0" data-id="element-349">
                      <DollarSign size={16} className={event.status === 'Paid in Full' ? 'text-green-600' : 'text-yellow-600'} strokeWidth={3} data-id="element-350" />
                    </div>}
                </div>
              </div>)}
          </div>
        </div>);
    }
    return <div data-id="element-351">
        <div className="flex items-center justify-between mb-6" data-id="element-352">
          <button onClick={previousMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-353">
            <ChevronLeft size={24} data-id="element-354" />
          </button>
          <h3 className="text-2xl font-bold text-slate-800" data-id="element-355">{monthName}</h3>
          <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-356">
            <ChevronRight size={24} data-id="element-357" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex gap-4 mb-4 text-sm" data-id="element-358">
          <div className="flex items-center gap-2" data-id="element-359">
            <div className="w-3 h-3 rounded bg-slate-400" data-id="element-360"></div>
            <span data-id="element-361">Pending</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-362">
            <div className="w-3 h-3 rounded bg-blue-500" data-id="element-363"></div>
            <span data-id="element-364">Confirmed</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-365">
            <div className="w-3 h-3 rounded bg-yellow-500" data-id="element-366"></div>
            <span data-id="element-367">Deposit</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-368">
            <div className="w-3 h-3 rounded bg-green-500" data-id="element-369"></div>
            <span data-id="element-370">Paid in Full</span>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-0 border border-slate-300 rounded-lg overflow-hidden" data-id="element-371">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="bg-slate-100 border-b border-slate-300 p-3 text-center font-semibold text-slate-700" data-id="element-372">
              {day}
            </div>)}
          {days}
        </div>
      </div>;
  };
  const renderListView = () => {
    const rentalsByMonthYear = rentals.reduce((acc, rental) => {
      if (!rental.proposedEventDate) {
        if (!acc['No Date']) acc['No Date'] = [];
        acc['No Date'].push(rental);
        return acc;
      }
      const date = new Date(rental.proposedEventDate);
      const monthYear = date.toLocaleString('default', {
        month: 'long',
        year: 'numeric'
      });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(rental);
      return acc;
    }, {} as Record<string, Rental[]>);
    const sortedMonthYears = Object.keys(rentalsByMonthYear).sort((a, b) => {
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      const dateA = new Date(rentalsByMonthYear[a][0].proposedEventDate);
      const dateB = new Date(rentalsByMonthYear[b][0].proposedEventDate);
      return dateA.getTime() - dateB.getTime();
    });
    return <div className="space-y-6" data-id="element-373">
        {sortedMonthYears.map(monthYear => {
        const monthRentals = rentalsByMonthYear[monthYear];
        return <div key={monthYear} className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-374">
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200" data-id="element-375">
                <div className="flex items-center justify-between" data-id="element-376">
                  <h3 className="text-lg font-semibold text-slate-800" data-id="element-377">
                    {monthYear}
                  </h3>
                  <span className="text-sm text-slate-600" data-id="element-378">
                    {monthRentals.length} event
                    {monthRentals.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="divide-y divide-slate-200" data-id="element-379">
                {monthRentals.sort((a, b) => {
              if (!a.proposedEventDate) return 1;
              if (!b.proposedEventDate) return -1;
              return new Date(a.proposedEventDate).getTime() - new Date(b.proposedEventDate).getTime();
            }).map(rental => <div key={rental.id} onClick={() => onRentalClick(rental)} className="p-4 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-380">
                      <div className="flex items-center justify-between" data-id="element-381">
                        <div className="flex-1 min-w-0" data-id="element-382">
                          <div className="flex items-center gap-3 mb-1" data-id="element-383">
                            <span className="font-semibold text-slate-800" data-id="element-384">
                              {rental.proposedEventDate ? (() => {
                        const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                        const startDateStr = startDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });
                        if (rental.selections.numberOfDays > 1) {
                          const endDate = new Date(startDate);
                          endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                          const endDateStr = endDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                          return `${startDateStr} - ${endDateStr}`;
                        }
                        return startDateStr;
                      })() : 'No date'}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-385">
                              {rental.status}
                            </span>
                            <span className="text-xs font-semibold text-blue-700" data-id="element-386">
                              {getActualRingCount(rental)} Ring
                              {getActualRingCount(rental) !== 1 ? 's' : ''}
                              {rental.selections.numberOfDays > 1 && <span className="text-purple-600 ml-1" data-id="element-387">
                                  ({rental.selections.numberOfDays} Days)
                                </span>}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600 truncate" data-id="element-388">
                            {rental.eventName} • {rental.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-1" data-id="element-389">
                            {[rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ') || 'No location'}
                          </div>
                        </div>
                        <div className="text-right ml-4" data-id="element-390">
                          <div className="font-semibold text-slate-800" data-id="element-391">
                            ${rental.total.toFixed(2)}
                          </div>
                          {rental.selections.requireTech && <div className="text-xs text-red-600 mt-1" data-id="element-392">
                              Tech Required
                            </div>}
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>;
      })}

        {sortedMonthYears.length === 0 && <div className="text-center py-12" data-id="element-393">
            <Calendar size={48} className="text-slate-300 mx-auto mb-4" data-id="element-394" />
            <p className="text-slate-600" data-id="element-395">No events scheduled</p>
          </div>}
      </div>;
  };
  return <div className="p-6" data-id="element-396">
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-6" data-id="element-397">
        <div className="flex gap-2" data-id="element-398">
          <button onClick={() => setCalendarView('month')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'month' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-399">
            Month View
          </button>
          <button onClick={() => setCalendarView('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-400">
            List View
          </button>
        </div>

        {calendarView === 'list' && <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-id="element-401">
            <input type="checkbox" checked={groupByCoast} onChange={e => setGroupByCoast(e.target.checked)} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" data-id="element-402" />
            <span data-id="element-403">Group by Coast</span>
          </label>}
      </div>

      {calendarView === 'month' ? renderCalendar() : renderListView()}
    </div>;
}